<script setup lang="ts">
import { migrateReconSeenId, writeReconSeenId } from '@/composables/useReconNotifications';

const { t, locale } = useI18n();
const router = useRouter();

const loading = ref(false);
const undoing = ref(false);
const pending = ref(0);
const pctMatched = ref<number | null>(null);
const lastActor = ref('');
const lastWhen = ref('');
const lastCanUndo = ref(false);
const hasNew = ref(false);
const snackbar = ref({ show: false, text: '', color: 'success' });

function formatRel(iso: string) {
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('dash.reconJustNow');
    if (mins < 60) return t('dash.reconMinsAgo', { n: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('dash.reconHoursAgo', { n: hrs });
    return d.toLocaleDateString(locale.value?.startsWith('en') ? 'en-US' : 'id-ID', {
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return iso;
  }
}

async function load() {
  loading.value = true;
  try {
    const [countRes, timeline] = await Promise.all([
      $fetch<any>('/api/reconciliation/count'),
      $fetch<any[]>('/api/reconciliation/timeline', { query: { limit: 3 } }),
    ]);
    pending.value = countRes?.pendingCount || 0;
    pctMatched.value = countRes?.digest?.pctMatched ?? null;
    const top = Array.isArray(timeline) ? timeline[0] : null;
    if (top) {
      lastActor.value = String(top.actor || '—');
      lastWhen.value = formatRel(String(top.createdAt || ''));
      lastCanUndo.value = !!top.canUndo;
      if (import.meta.client) {
        const seen = migrateReconSeenId();
        hasNew.value = Number(top.id) > seen;
      }
    } else {
      lastCanUndo.value = false;
    }
  } catch {
    pending.value = 0;
    lastCanUndo.value = false;
  } finally {
    loading.value = false;
  }
}

function openRecon() {
  if (import.meta.client) {
    const topId = migrateReconSeenId();
    writeReconSeenId(topId || Date.now());
    hasNew.value = false;
  }
  router.push('/reconciliation');
}

async function undoLast(e: Event) {
  e.stopPropagation();
  if (undoing.value || !lastCanUndo.value) return;
  undoing.value = true;
  try {
    const res = await $fetch<{ undone: string; removedCount: number }>('/api/reconciliation/undo', {
      method: 'POST',
    });
    snackbar.value = {
      show: true,
      text: t('dash.reconUndoOk'),
      color: 'success',
    };
    if (import.meta.client) {
      window.dispatchEvent(new CustomEvent('recon-count', { detail: { refresh: true } }));
    }
    await load();
    void res;
  } catch (err: any) {
    snackbar.value = {
      show: true,
      text: err?.data?.statusMessage || err?.message || t('dash.reconUndoFail'),
      color: 'error',
    };
  } finally {
    undoing.value = false;
  }
}

onMounted(() => {
  void load();
  if (import.meta.client) {
    window.addEventListener('recon-count', load);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void load();
    });
  }
});

onBeforeUnmount(() => {
  if (import.meta.client) window.removeEventListener('recon-count', load);
});
</script>

<template>
  <div class="sb-recon" :class="{ 'sb-recon--new': hasNew, 'sb-recon--warn': pending > 0 }">
    <button type="button" class="sb-recon__btn" @click="openRecon">
      <span class="sb-recon__ico">
        <v-icon size="16">mdi-swap-horizontal-bold</v-icon>
        <span v-if="hasNew" class="sb-recon__dot" />
      </span>
      <span class="sb-recon__main">
        <strong>{{ t('dash.reconRecentTitle') }}</strong>
        <span v-if="lastWhen" class="sb-recon__sub">
          {{ lastWhen }} · {{ lastActor }}
        </span>
        <span v-else class="sb-recon__sub">{{ t('dash.reconRecentEmpty') }}</span>
      </span>
      <span class="sb-recon__stats">
        <span v-if="pctMatched != null" class="sb-recon__pct">{{ pctMatched.toFixed(0) }}%</span>
        <span v-if="pending > 0" class="sb-recon__pending">{{ pending }}</span>
      </span>
      <v-icon size="14" class="sb-recon__go">mdi-chevron-right</v-icon>
    </button>
    <button
      v-if="lastCanUndo"
      type="button"
      class="sb-recon__undo"
      :disabled="undoing"
      :title="t('dash.reconUndoLastHint')"
      @click="undoLast"
    >
      <v-icon size="14">{{ undoing ? 'mdi-loading' : 'mdi-undo' }}</v-icon>
      <span>{{ t('dash.reconUndoLast') }}</span>
    </button>
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3200" location="bottom">
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<style scoped>
.sb-recon {
  margin: 0 12px 10px;
  border-radius: 12px;
  border: 1px solid rgba(15, 118, 110, 0.2);
  background: linear-gradient(135deg, rgba(240, 253, 250, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%);
  overflow: hidden;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.sb-recon--new {
  border-color: rgba(245, 158, 11, 0.45);
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.12);
}
.sb-recon--warn {
  border-color: rgba(220, 38, 38, 0.25);
}
.sb-recon__btn {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 10px 10px 8px;
  border: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}
.sb-recon__btn:hover {
  background: rgba(15, 118, 110, 0.05);
}
.sb-recon__ico {
  position: relative;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #ccfbf1;
  color: #0f766e;
}
.sb-recon__dot {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #f59e0b;
  border: 1.5px solid #fff;
}
.sb-recon__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.sb-recon__main strong {
  font-size: 12px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}
.sb-recon__sub {
  font-size: 10px;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sb-recon__stats {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.sb-recon__pct {
  font-size: 11px;
  font-weight: 800;
  color: #0f766e;
}
.sb-recon__pending {
  min-width: 18px;
  padding: 1px 5px;
  border-radius: 999px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 10px;
  font-weight: 800;
  text-align: center;
}
.sb-recon__go {
  color: #94a3b8;
  flex-shrink: 0;
}
.sb-recon__undo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: calc(100% - 16px);
  margin: 0 8px 8px;
  padding: 5px 8px;
  border-radius: 8px;
  border: 1px solid #fcd34d;
  background: #fffbeb;
  color: #b45309;
  font-size: 10px;
  font-weight: 800;
  cursor: pointer;
}
.sb-recon__undo:hover:not(:disabled) {
  background: #fef3c7;
}
.sb-recon__undo:disabled {
  opacity: 0.6;
  cursor: wait;
}
</style>
